import { FileText, PenLine, Search, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useActs, useDocuments, useClientRole, useProjects, useSignRequests, useUpload } from '@/api';
import { clientCan } from '@/lib/access';
import { hasActs, useContour } from '@/lib/contour';
import type { Act, SignRequest } from '@/api/types';
import { DownloadButton } from '@/components/DownloadButton';
import { ActSignDialog, ownerSignsAct } from '@/components/ActSignDialog';
import { SignDialog } from '@/components/SignDialog';
import { Status, Tabs } from '@/components/ui';
import { tabPanel } from '@/lib/tabs';
import type { DocumentKind } from '@/api/types';
import { Empty, PageHeader } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { errorMessage } from '@/lib/errors';
import { formatSize, formatWhen } from '@/lib/format';
import { ALLOWED_EXTENSIONS, precheck } from '@/lib/upload';

type UploadItem = { id: number; name: string; status: 'uploading' | 'done' | 'error'; error?: string };

/** Hidden file input behind a button. Each file is checked, then sent in its own request, one after another, with its own status line. */
export function UploadButton({ projectId, taskId, label = 'Загрузить файл', onUploaded, compact }: { projectId?: string; taskId?: string; label?: string; onUploaded?: () => void; compact?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUpload();
  const [items, setItems] = useState<UploadItem[]>([]);
  const seq = useRef(0);
  const set = (id: number, patch: Partial<UploadItem>) => setItems((all) => all.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  async function send(files: File[]) {
    const queued = files.map((f) => ({ f, id: ++seq.current, problem: precheck(f) }));
    setItems((all) => [
      ...all.filter((x) => x.status === 'uploading'),
      ...queued.map(({ f, id, problem }): UploadItem => (problem ? { id, name: f.name, status: 'error', error: problem } : { id, name: f.name, status: 'uploading' })),
    ]);
    let uploaded = false;
    for (const { f, id, problem } of queued) {
      if (problem) continue;
      try {
        await upload.mutateAsync({ projectId, taskId, name: f.name, sizeKb: Math.max(1, Math.round(f.size / 1024)), file: f });
        set(id, { status: 'done' });
        uploaded = true;
      } catch (e) {
        set(id, { status: 'error', error: errorMessage(e) });
      }
    }
    // Only a file that really went up counts: callers use this to tick a step off.
    if (uploaded) onUploaded?.();
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept={ALLOWED_EXTENSIONS.map((x) => `.${x}`).join(',')}
        className="hidden"
        data-testid="upload-input"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length) void send(files);
        }}
      />
      <button className={compact ? 'btn-secondary h-7 px-2.5 text-xs' : 'btn-secondary'} onClick={() => input.current?.click()} disabled={upload.isPending}>
        {!compact && <Upload className="size-4" />} {upload.isPending ? 'Загружаем…' : label}
      </button>
      {items.length > 0 && (
        <ul className="basis-full text-sm" aria-label="Загрузка файлов">
          {items.map((x) => (
            <li key={x.id} className="flex items-baseline gap-2 py-0.5" data-status={x.status}>
              <span className="truncate">{x.name}</span>
              {x.status === 'uploading' && <span className="text-muted">загружается…</span>}
              {x.status === 'done' && <span className="text-done">загружен</span>}
              {x.status === 'error' && (
                <span className="text-overdue" role="alert">
                  не загружен: {x.error}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const kinds: { value: DocumentKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'contract', label: 'Договоры' },
  { value: 'sign', label: 'На подпись' },
  { value: 'act', label: 'Акты' },
  { value: 'brief', label: 'Брифы' },
  { value: 'design', label: 'Макеты' },
  { value: 'report', label: 'Отчёты' },
  { value: 'other', label: 'Материалы' },
];

export function DocumentList({ projectId }: { projectId?: string }) {
  const withActs = hasActs(useContour());
  const docsQ = useDocuments(projectId);
  const docs = docsQ.data;
  const { data: projects } = useProjects(clientCan(useClientRole(), 'work'));
  const [kind, setKind] = useState<DocumentKind | 'all'>('all');
  const [q, setQ] = useState('');
  const list = useMemo(
    () => docs?.filter((d) => (kind === 'all' || d.kind === kind || (kind === 'act' && d.kind === 'act_scan')) && (!q.trim() || d.name.toLowerCase().includes(q.trim().toLowerCase()))),
    [docs, kind, q],
  );
  const present = new Set<DocumentKind>(docs?.map((d) => (d.kind === 'act_scan' ? 'act' : d.kind)));
  const { data: acts } = useActs();
  const [signingAct, setSigningAct] = useState<Act>();

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <UploadButton projectId={projectId} />
        <label className="relative w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Найти файл" className="field h-8 py-0 pl-8 text-sm" />
        </label>
        {kinds
          .filter((k) => k.value === 'all' || present.has(k.value as DocumentKind))
          .map((k) => (
            <button key={k.value} className="chip" onClick={() => setKind(k.value)} aria-pressed={kind === k.value}>
              {k.label}
            </button>
          ))}
      </div>
      {!list ? (
        <QueryPending query={docsQ} />
      ) : list.length === 0 ? (
        <Empty title="Здесь пока пусто">{withActs ? 'Брифы, макеты, отчёты и акты появятся, когда команда их выложит.' : 'Брифы, макеты и отчёты появятся, когда команда их выложит.'}</Empty>
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {list.map((d) => (
            <li key={d.id} className="flex items-center gap-4 py-3">
              <FileText className="size-4 shrink-0 text-muted" />
              <div className="min-w-0 flex-1">
                <div className="truncate">{d.name}</div>
                {!projectId && <div className="truncate text-sm text-muted">{projects?.find((p) => p.id === d.projectId)?.title ?? 'Общие документы'}</div>}
              </div>
              <span className="hidden text-sm text-muted md:block">{d.uploadedBy.side === 'client' ? 'вы' : d.uploadedBy.name}</span>
              <span className="hidden w-32 text-right text-sm text-faint sm:block">{formatWhen(d.uploadedAt)}</span>
              <span className="num w-16 text-right text-sm text-faint">{formatSize(d.sizeKb)}</span>
              {withActs && <ActState act={acts?.find((a) => a.documentId === d.id)} onSign={setSigningAct} />}
              <DownloadButton name={d.name} load={() => api.downloadDocument(d.id)} />
            </li>
          ))}
        </ul>
      )}
      {signingAct && <ActSignDialog act={signingAct} onClose={() => setSigningAct(undefined)} />}
    </>
  );
}

/** Next to an act's PDF: where it stands, and the way to sign it (the owner opens the signing dialog right here). */
function ActState({ act, onSign }: { act?: Act; onSign: (a: Act) => void }) {
  const owner = useClientRole() === 'owner';
  if (!act) return null;
  if (act.status === 'to_sign')
    return owner ? (
      <button className="btn-primary h-7 px-2.5 text-xs" onClick={() => onSign(act)}>
        Подписать
      </button>
    ) : (
      <span className="text-xs text-muted">{ownerSignsAct}</span>
    );
  return act.status === 'signed' ? <Status tone="done">Подписан</Status> : null;
}

function SignList() {
  const withActs = hasActs(useContour());
  const signQ = useSignRequests();
  const { data } = signQ;
  const [signing, setSigning] = useState<SignRequest>();
  if (!data) return <QueryPending query={signQ} />;
  const order = [...data].sort((a, b) => (a.status === b.status ? b.createdAt.localeCompare(a.createdAt) : a.status === 'to_sign' ? -1 : 1));
  return (
    <>
      <ul className="divide-y divide-line border-y border-line">
        {order.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-4 py-3">
            <PenLine className="size-4 shrink-0 text-muted" />
            <span className="min-w-0 flex-1">
              <button type="button" className="block text-left font-medium underline-offset-2 hover:underline" onClick={() => setSigning(s)}>
                {s.title}
              </button>
              <span className="block text-sm text-muted">
                {s.status === 'signed' ? `Подписан ${formatWhen(s.signedAt!)}, ${s.signedBy}, отпечаток ${s.hash}` : `Отправлен на подпись ${formatWhen(s.createdAt)}`}
              </span>
            </span>
            {s.status === 'to_sign' ? (
              <button className="btn-primary" onClick={() => setSigning(s)}>
                Подписать
              </button>
            ) : (
              <>
                <Status tone="done">Подписан</Status>
                <button className="btn-secondary" onClick={() => setSigning(s)}>
                  Открыть
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {withActs && <p className="mt-3 text-sm text-muted">Акты, которые отправляет студия, подписывает владелец кабинета: здесь или в разделе «Деньги».</p>}
      {signing && <SignDialog request={signing} onClose={() => setSigning(undefined)} />}
    </>
  );
}

export function Documents() {
  const withActs = hasActs(useContour());
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'sign' ? 'sign' : 'files';
  const { data: sign } = useSignRequests();
  return (
    <>
      <PageHeader title="Документы" description={`${withActs ? 'Договоры, акты, макеты и отчёты' : 'Договоры, макеты и отчёты'} по всем проектам. Файлы, которые вы присылаете в задачах, тоже попадают сюда.`} />
      <Tabs
        id="documents"
        label="Документы"
        value={tab}
        onChange={(t) => setParams(t === 'sign' ? { tab: 'sign' } : {})}
        items={[
          { value: 'files', label: 'Файлы' },
          { value: 'sign', label: 'На подпись', count: sign?.filter((s) => s.status === 'to_sign').length },
        ]}
      />
      <div {...tabPanel('documents', tab)} className="focus:outline-none">
        {tab === 'files' ? <DocumentList /> : <SignList />}
      </div>
    </>
  );
}
